import "./global.css";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Suspense, lazy, useEffect } from "react";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import LoadingScreen from "./components/LoadingScreen";
import RoleDashboard from "./pages/RoleDashboard";
const Index = lazy(() => import("./pages/Index"));
const Home = lazy(() => import("./pages/Home"));
const Welcome = lazy(() => import("./pages/Welcome"));
const SignIn = lazy(() => import("./pages/SignIn"));
const SignUp = lazy(() => import("./pages/SignUp"));
const Profile = lazy(() => import("./pages/Profile"));
const MyProfile = lazy(() => import("./pages/MyProfile"));
const SavedOpportunities = lazy(() => import("./pages/SavedOpportunities"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Application = lazy(() => import("./pages/Application"));
const Settings = lazy(() => import("./pages/Settings"));
const Search = lazy(() => import("./pages/Search"));
const Messages = lazy(() => import("./pages/Messages"));
const Associates = lazy(() => import("./pages/Associates"));
const Community = lazy(() => import("./pages/Community"));
const CommunityPage = lazy(() => import("./pages/CommunityPage"));
const MyApplications = lazy(() => import("./pages/MyApplications"));
const MyOpportunities = lazy(() => import("./pages/MyOpportunities"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const Jobs = lazy(() => import("./pages/Jobs"));
const Events = lazy(() => import("./pages/Events"));
const AiResumeBuilder = lazy(() => import("./pages/AiResumeBuilder"));
const CareerDreamer = lazy(() => import("./pages/CareerDreamer"));
const AITools = lazy(() => import("./pages/AITools"));
const AiResearch = lazy(() => import("./pages/AiResearch"));
const AiStartup = lazy(() => import("./pages/AiStartup"));

const SupabaseDemo = lazy(() => import("./pages/SupabaseDemo"));
const EnvTest = lazy(() => import("./pages/EnvTest"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ProfileSelection = lazy(() => import("./pages/ProfileSelection"));
const EmailVerification = lazy(() => import("./pages/EmailVerification"));
const VerifyEmailSuccess = lazy(() => import("./pages/VerifyEmailSuccess"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

const AuthorDashboard = lazy(() => import("./pages/AuthorDashboard"));
/* switched to static import to avoid dynamic import fetch issues in dev */
// const RoleDashboard = lazy(() => import("./pages/RoleDashboard"));
const Discovery = lazy(() => import("./pages/Discovery"));
const Funding = lazy(() => import("./pages/Funding"));
const UniversityMou = lazy(() => import("./pages/UniversityMou"));
const UniversityVerifications = lazy(() => import("./pages/UniversityVerifications"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const GuestOpportunities = lazy(() => import("./pages/GuestOpportunities"));
const SignUpPrompt = lazy(() => import("./pages/SignUpPrompt"));
const OpportunityApplications = lazy(() => import("./pages/OpportunityApplications"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

const queryClient = new QueryClient();

// Protected Route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <LoadingScreen message="Verifying session..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  // Require verified email before accessing protected pages
  if (user && !user.email_confirmed) {
    const email = encodeURIComponent(user.email || '');
    return <Navigate to={`/email-verification?email=${email}`} replace />;
  }

  return <>{children}</>;
};

// Protected Route that requires profile completion
const ProfileRequiredRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen message="Checking profile..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  // Require verified email before accessing profile-required pages
  if (user && !user.email_confirmed) {
    const email = encodeURIComponent(user.email || '');
    return <Navigate to={`/email-verification?email=${email}`} replace />;
  }

  // Check if user has completed profile setup
  // If user doesn't have profile_type or it is 'Other', redirect to profile selection
  // But don't redirect if we're already on profile-selection page to prevent loops
  if (user && (!user.profile_type || user.profile_type === 'Other') && location.pathname !== '/profile-selection') {
    console.log('🔄 User missing profile_type, redirecting to profile selection:', user);
    return <Navigate to="/profile-selection" replace />;
  }

  return <>{children}</>;
};

// Profile Selection Route wrapper (redirect to home if profile already completed)
const ProfileSelectionRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <LoadingScreen message="Checking profile status..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  // Check if user has verified email
  if (user && !user.email_confirmed) {
    const email = encodeURIComponent(user.email || '');
    return <Navigate to={`/email-verification?email=${email}`} replace />;
  }

  // If user already has profile_type (and not 'Other'), redirect to home (profile already completed)
  if (user && user.profile_type && user.profile_type !== 'Other') {
    console.log('✅ User already has profile_type, redirecting to home:', user.profile_type);
    return <Navigate to="/home" replace />;
  }

  console.log('🔄 User needs to complete profile selection:', user);
  return <>{children}</>;
};

// Auth Route wrapper (redirect to home if already logged in)
const AuthRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    // Allow auth pages (signin/signup/welcome) to render while auth initializes to avoid infinite loading
    return <>{children}</>;
  }

  if (isAuthenticated) {
    // If user has profile_type (and not 'Other'), go to home; otherwise allow auth pages (welcome/signin/signup) to render
    if (user?.profile_type && user.profile_type !== 'Other') {
      console.log('✅ Authenticated user with profile_type, redirecting to home:', user.profile_type);
      return <Navigate to="/home" replace />;
    }
    return <>{children}</>;
  }

  return <>{children}</>;
};

// Guest Protected Route wrapper (redirects guests to sign-up prompt)
const GuestProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen message="Checking access..." />;
  }

  // If not authenticated, redirect to sign-up prompt
  if (!isAuthenticated) {
    return <Navigate to="/signup-prompt" replace />;
  }

  return <>{children}</>;
};

const AppRoutes = () => {
  const { user } = useAuth();

  // Track Google Analytics User ID
  useEffect(() => {
    if (user?.id && typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('config', 'G-R4FXCCLMVZ', {
        'user_id': user.id
      });
    }
  }, [user]);

  return (
    <BrowserRouter>
      <Suspense
        fallback={<LoadingScreen message="Loading page..." />}
      >
        <Routes>
          <Route
            path="/"
            element={
              <AuthRoute>
                <Welcome />
              </AuthRoute>
            }
          />
          <Route
            path="/signin"
            element={
              <AuthRoute>
                <SignIn />
              </AuthRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <AuthRoute>
                <SignUp />
              </AuthRoute>
            }
          />
          <Route path="/email-verification" element={<EmailVerification />} />
          <Route path="/verify-email-success" element={<VerifyEmailSuccess />} />
          <Route path="/auth/confirm" element={<EmailVerification />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/profile-selection"
            element={
              <ProfileSelectionRoute>
                <ProfileSelection />
              </ProfileSelectionRoute>
            }
          />
          <Route
            path="/home"
            element={
              <ProfileRequiredRoute>
                <Home />
              </ProfileRequiredRoute>
            }
          />
          <Route
            path="/opportunities"
            element={
              <ProfileRequiredRoute>
                <Index />
              </ProfileRequiredRoute>
            }
          />
          <Route
            path="/discovery"
            element={
              <ProfileRequiredRoute>
                <Discovery />
              </ProfileRequiredRoute>
            }
          />
          <Route
            path="/guest-opportunities"
            element={<GuestOpportunities />}
          />
          <Route
            path="/signup-prompt"
            element={<SignUpPrompt />}
          />
          <Route
            path="/profile/:userId"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-profile"
            element={
              <ProtectedRoute>
                <MyProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/saved-opportunities"
            element={
              <ProtectedRoute>
                <SavedOpportunities />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/apply/:opportunityId"
            element={
              <ProtectedRoute>
                <Application />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <Search />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/associates"
            element={
              <ProtectedRoute>
                <Associates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invideo"
            element={
              <GuestProtectedRoute>
                <Community />
              </GuestProtectedRoute>
            }
          />
          <Route
            path="/invideo/:id"
            element={
              <GuestProtectedRoute>
                <CommunityPage />
              </GuestProtectedRoute>
            }
          />
          <Route
            path="/my-applications"
            element={
              <ProtectedRoute>
                <MyApplications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-opportunities"
            element={
              <ProtectedRoute>
                <MyOpportunities />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contact-us"
            element={
              <ProtectedRoute>
                <ContactUs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs"
            element={
              <ProtectedRoute>
                <Jobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/events"
            element={
              <ProtectedRoute>
                <Events />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-resume-builder"
            element={
              <ProtectedRoute>
                <AiResumeBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/career-dreamer"
            element={
              <ProtectedRoute>
                <CareerDreamer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-tools"
            element={
              <ProtectedRoute>
                <AITools />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-research"
            element={
              <ProtectedRoute>
                <AiResearch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-startup"
            element={
              <ProtectedRoute>
                <AiStartup />
              </ProtectedRoute>
            }
          />

          <Route
            path="/supabase-demo"
            element={
              <ProtectedRoute>
                <SupabaseDemo />
              </ProtectedRoute>
            }
          />
          <Route path="/env-test" element={<EnvTest />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />

          <Route
            path="/author"
            element={
              <ProtectedRoute>
                <AuthorDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/opportunities/:id/applications"
            element={
              <ProtectedRoute>
                <OpportunityApplications />
              </ProtectedRoute>
            }
          />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <RoleDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/discovery"
            element={
              <ProfileRequiredRoute>
                <Discovery />
              </ProfileRequiredRoute>
            }
          />
          <Route
            path="/funding"
            element={
              <ProfileRequiredRoute>
                <Funding />
              </ProfileRequiredRoute>
            }
          />
          <Route
            path="/university/mou"
            element={
              <ProfileRequiredRoute>
                <UniversityMou />
              </ProfileRequiredRoute>
            }
          />
          <Route
            path="/university/verifications"
            element={
              <ProfileRequiredRoute>
                <UniversityVerifications />
              </ProfileRequiredRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter >
  );
};

const App = () => {
  console.log('🚀 App component is rendering');

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <AppRoutes />
            <Analytics />
            <SpeedInsights />
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

// Add global error handler for React rendering issues
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    console.error('🚨 Global React error:', event.error);
  });

  window.addEventListener('unhandledrejection', (event) => {
    console.error('🚨 Unhandled promise rejection:', event.reason);
  });
}

export default App;
